import Lottie from "lottie-web";
import { motionFrame, motionPoint, motionLerp, motionState } from "./motionCore";

export { motionPoint, motionFrame, motionState };

// Move a layer from where it is placed on the canvas (A) to an end point (Z).
//
// Stored on the layer as `Motion` (positions are the layer's top-left corner as a fraction of the
// canvas width/height, so canvas zoom, video shape and export size do not matter):
//   toXf, toYf  Z, the end point
//   delay       seconds after the layer appears before it starts moving
//   duration    seconds the move takes (the speed)
//   easing      "linear" (steady, best for walking and throws) or "smooth" (eases in and out)
//   path        "line" (default) | "arc" (a throw: projectile curve through the handle)
//               | "bounce" (hops from A to Z; the handle is the top of the first hop)
//               | "custom" (a smooth curve through `points`, drawn or placed with the mouse)
//   hXf, hYf    the curve handle (arc / bounce)
//   bounces     number of hops (bounce), bounciness 0.2..1 (each hop's height relative to the last)
//   points      [{ xf, yf }] the points between A and Z (custom)
//   spin        turns while moving (a rolling ball), negative = anticlockwise
//   repeat      "once" | "pingpong" (there and back) | "loop" (starts again at A); times = trips
//               (0 = until the layer ends); pause = seconds to wait at each end
//   scaleTo     size at Z (1 = unchanged), opacityTo = opacity at Z (0..1)
//   autoFace    a character turns to face the way it travels
//   walkFile    a character's action (e.g. its walk) played while it travels, then back to its own
// The maths is in motionCore.js (also copied into the export page).
// Before the move the layer is at A, after it the layer stays at Z until the layer ends.
//
// The same maths runs in the export page (AnimationStudio2_API/Assets/Export/Export.js,
// toonsApplyMotion), so the editor, the preview and the exported video move the same way.

export const MOTION_EASINGS = [
  { value: "linear", label: "Steady (walking)" },
  { value: "smooth", label: "Smooth start and stop" },
];

// Canvas widths per second for the speed presets.
export const MOTION_SPEEDS = [
  { label: "Slow", widthsPerSecond: 0.08 },
  { label: "Normal", widthsPerSecond: 0.16 },
  { label: "Fast", widthsPerSecond: 0.32 },
];

export const MIN_MOTION_SECONDS = 0.2;

export function hasMotion(layer) {
  const m = layer && layer.Motion;
  return (
    !!m &&
    m.enabled !== false &&
    Number.isFinite(Number(m.toXf)) &&
    Number.isFinite(Number(m.toYf)) &&
    Number(m.duration) > 0
  );
}

/** 0..1: how far along the path the layer is, `seconds` after the layer appeared. */
export function motionProgress(motion, seconds) {
  return motionState(motion, seconds).p;
}

function restingPosition(el) {
  const left = parseFloat(el.style.left);
  const top = parseFloat(el.style.top);
  return {
    x: Number.isFinite(left) ? left : el.offsetLeft,
    y: Number.isFinite(top) ? top : el.offsetTop,
  };
}

// While a character travels, its walk action plays in a layer of its own on top of the character
// (the character's own animation is hidden meanwhile).
function setWalk(el, layer, url, f) {
  let w = el.__toonsWalk;
  const want = f.moving && url;
  if (w && w.url !== url) {
    try {
      w.anim.destroy();
    } catch (e) {
      /* already gone */
    }
    w.div.remove();
    el.__toonsWalk = w = null;
  }
  if (want && !w) {
    const div = document.createElement("div");
    div.className = "toonsWalk";
    div.style.cssText = "position:absolute;inset:0;pointer-events:none;display:none;";
    div.style.transform = layer.flip || "";
    el.appendChild(div);
    const anim = Lottie.loadAnimation({ container: div, renderer: "svg", loop: true, autoplay: false, path: url });
    w = el.__toonsWalk = { url, div, anim, ready: false };
    anim.addEventListener("DOMLoaded", () => {
      w.ready = true;
    });
  }
  const show = !!(want && w && w.ready);
  if (w) {
    w.div.style.display = show ? "block" : "none";
    if (show && w.anim.totalFrames > 0) {
      w.anim.goToAndStop((f.walkSec * (w.anim.frameRate || 30)) % w.anim.totalFrames, true);
    }
  }
  for (const child of Array.from(el.children)) {
    if (w && child === w.div) continue;
    if (show) {
      if (child.dataset.toonsHidden === undefined) {
        child.dataset.toonsHidden = child.style.visibility || "";
        child.style.visibility = "hidden";
      }
    } else if (child.dataset.toonsHidden !== undefined) {
      child.style.visibility = child.dataset.toonsHidden;
      delete child.dataset.toonsHidden;
    }
  }
}

/** Draws one frame of a layer's move on its element (editor / preview). */
export function applyMotionFrame(el, layer, sec, assetBase) {
  if (!el || !el.offsetParent) return;
  const canvas = el.offsetParent;
  const rest = restingPosition(el);
  const f = motionFrame(layer.Motion, rest, canvas.clientWidth, canvas.clientHeight, sec);
  el.style.translate = f.started ? `${f.dx.toFixed(2)}px ${f.dy.toFixed(2)}px` : "";
  el.style.rotate = f.rotate ? `${f.rotate.toFixed(2)}deg` : "";
  const sx = f.scale * (f.flip ? -1 : 1);
  el.style.scale = sx !== 1 || f.scale !== 1 ? `${sx.toFixed(4)} ${f.scale.toFixed(4)}` : "";
  el.style.filter = f.opacity < 1 ? `opacity(${f.opacity.toFixed(3)})` : "";
  const walkFile = layer.type === "Character" && layer.Motion.walkFile;
  if (walkFile || el.__toonsWalk) {
    const url = walkFile ? (/^https?:/i.test(walkFile) ? walkFile : (assetBase || "") + "/" + walkFile) : null;
    setWalk(el, layer, url, f);
  }
}

/**
 * Moves the layers' elements (`idPrefix` + AnimationId) to where they are at `nowMs` on the
 * timeline. Uses the CSS translate / rotate / scale properties (which add to the element's own
 * position, rotation and flip) and filter: opacity(), so clearing them puts the layer back at A.
 * `assetBase` (the API address) is needed for a character's walk action.
 */
export function applyLayerMotion(layers, nowMs, onePxMs, idPrefix, assetBase) {
  if (!Array.isArray(layers) || !(onePxMs > 0)) return;
  for (const layer of layers) {
    if (!hasMotion(layer)) continue;
    const el = document.getElementById(idPrefix + layer.AnimationId);
    if (!el) continue;
    const startMs = (Number(layer.startPositionOnTimeline) || 0) * onePxMs;
    applyMotionFrame(el, layer, (nowMs - startMs) / 1000, assetBase);
  }
}

/** Puts every moved element back at A (when playback stops). */
export function clearLayerMotion(idPrefix) {
  document.querySelectorAll(`[id^="${idPrefix}"]`).forEach((el) => {
    if (el.style.translate) el.style.translate = "";
    if (el.style.rotate) el.style.rotate = "";
    if (el.style.scale) el.style.scale = "";
    if (el.style.filter && el.style.filter.indexOf("opacity(") === 0) el.style.filter = "";
    if (el.__toonsWalk) setWalk(el, {}, el.__toonsWalk.url, { moving: false, walkSec: 0 });
  });
}

/** Seconds a move takes in total (all trips and pauses), or Infinity when it repeats until the end. */
export function motionTotalSeconds(m) {
  const dur = Number(m.duration) || 0;
  if (m.repeat !== "pingpong" && m.repeat !== "loop") return dur;
  const times = Math.round(Number(m.times) || 0);
  if (!times) return Infinity;
  return times * dur + (times - 1) * (Number(m.pause) || 0);
}

/** A character's actions that suit walking, best first. */
export function walkActions(layer) {
  const poses = (layer && Array.isArray(layer.character_poses) ? layer.character_poses : []).filter((p) => p && p.poseFile);
  const rank = (n) => {
    const s = String(n || "").toLowerCase();
    if (/side.?walk/.test(s)) return 0;
    if (/^walk(ing)?$/.test(s)) return 1;
    if (/walk/.test(s)) return 2;
    if (/^run(ning)?$/.test(s)) return 3;
    return 9;
  };
  return poses.slice().sort((a, b) => rank(a.poseName) - rank(b.poseName)).map((p) => ({ name: p.poseName, file: p.poseFile, walkLike: rank(p.poseName) < 9 }));
}

/** Seconds the layer is on screen. */
export function layerSeconds(layer, onePxMs) {
  return ((Number(layer.widthOnTimeline) || 0) * onePxMs) / 1000;
}

/** A first end point: 35% of the canvas to the right (or left when there is no room). */
export function defaultMotion(layer, canvasWidth, canvasHeight, onePxMs) {
  const shift = canvasWidth * 0.35;
  const x = Number(layer.x) || 0;
  const w = Number(layer.width) || 0;
  const toX = x + w + shift <= canvasWidth ? x + shift : Math.max(0, x - shift);
  const length = layerSeconds(layer, onePxMs);
  const duration = length > 0 ? Math.max(MIN_MOTION_SECONDS, Math.min(3, length)) : 3;
  return {
    enabled: true,
    toXf: toX / canvasWidth,
    toYf: (Number(layer.y) || 0) / canvasHeight,
    delay: 0,
    duration: Math.round(duration * 10) / 10,
    easing: "linear",
    ...characterDefaults(layer),
  };
}

/** A character walks (its walk action, if it has one) and faces the way it goes. */
export function characterDefaults(layer) {
  if (!layer || layer.type !== "Character") return {};
  const walk = walkActions(layer).find((a) => a.walkLike);
  return { autoFace: true, walkFile: walk ? walk.file : null };
}

/** Duration for a speed preset over the current distance (in canvas widths). */
export function durationForSpeed(distanceInWidths, widthsPerSecond) {
  const seconds = distanceInWidths / widthsPerSecond;
  return Math.round(Math.max(MIN_MOTION_SECONDS, Math.min(60, seconds)) * 10) / 10;
}

/**
 * Layers that used the old, editor-only "A to Z" (AzData: end box in canvas pixels at 100% zoom)
 * get the same end point as a Motion. Returns true when something changed.
 */
export function convertOldAtoZ(layers, canvasWidth, canvasHeight, onePxMs) {
  let changed = false;
  if (!Array.isArray(layers) || !(canvasWidth > 0) || !(canvasHeight > 0)) return false;
  for (const layer of layers) {
    if (!layer || !layer.AzData) continue;
    const az = layer.AzData;
    if (!layer.Motion && Number.isFinite(Number(az.x)) && Number.isFinite(Number(az.y))) {
      const seconds = ((Number(az.widthOnTimeline) || 0) * onePxMs) / 1000;
      layer.Motion = {
        enabled: true,
        toXf: Number(az.x) / canvasWidth,
        toYf: Number(az.y) / canvasHeight,
        delay: 0,
        duration: seconds > 0 ? Math.round(seconds * 10) / 10 : 3,
        easing: "linear",
      };
    }
    delete layer.AzData;
    layer.startAtoZ = false;
    changed = true;
  }
  return changed;
}

export const MOTION_PATHS = [
  { value: "line", label: "Straight", icon: "trending_flat" },
  { value: "arc", label: "Throw (curve)", icon: "sports_baseball" },
  { value: "bounce", label: "Bounce", icon: "sports_volleyball" },
  { value: "custom", label: "Draw a path", icon: "gesture" },
];

/** A first handle for a throw / bounce: above the middle of A → Z (a quarter of the distance up). */
export function defaultHandle(A, Z, W, H) {
  const mid = motionLerp(A, Z, 0.5);
  const up = Math.max(Math.hypot(Z.x - A.x, Z.y - A.y) * 0.35, H * 0.15);
  return { hXf: mid.x / W, hYf: Math.max(0, mid.y - up) / H };
}
